import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { colors } from "../theme";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { dayTemplatesByCity } from "../data/dayTemplatesByCity";
import { getGoogleReservationLink, isRestaurant, getDefaultReservationTime } from "../services/restaurantService";
import { FiMapPin, FiClock, FiExternalLink, FiX, FiStar, FiPlus, FiMap, FiInfo, FiDollarSign, FiEdit2, FiCheck, FiAlertCircle, FiTrash2, FiCalendar, FiNavigation, FiChevronDown, FiChevronUp, FiMenu, FiSearch } from "react-icons/fi";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PlaceCache } from "../data/placeCache";
import MapComponent from './MapComponent';
import TransportSegment from './TransportSegment'; // Add this line

// Base Button component with Portuguese colors
const Button = styled.button`
  background: linear-gradient(135deg, #E31B23, #FF6B6B);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.8rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  box-shadow: 0 4px 12px rgba(227, 27, 35, 0.2);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #006E33, #00A854);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  & > * {
    position: relative;
    z-index: 1;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(227, 27, 35, 0.3);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    &::after {
      display: none;
    }
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: 768px) {
    width: ${props => props.fullWidth ? '100%' : 'auto'};
    padding: ${props => props.size === 'small' ? '0.5rem' : '0.8rem 1rem'};
  }
`;

// Get Mapbox token from environment
const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Helper: Haversine distance between two lat/lng
function getDistance(a, b) {
  if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null) return 0;
  const toRad = x => x * Math.PI / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const aVal = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.sin(dLng/2) * Math.sin(dLng/2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1-aVal));
  return R * c;
}

// City coordinates for transport planning (in [lng, lat] format for Mapbox)
const CITY_COORDINATES = {
  lisbon: [-9.1393, 38.7223],
  porto: [-8.6291, 41.1579],
  sintra: [-9.3783, 38.8009],
  algarve: [-8.6741, 37.1028],
  douro: [-7.7978, 41.1029],
  coimbra: [-8.4292, 40.2113],
};

// Transport options between cities with more accurate information
const TRANSPORT_OPTIONS = {
  // Lisbon to/from Porto
  'lisbon-porto': {
    train: { duration: '2h 52m', distance: '313 km', price: '€25-65', frequency: '15+ daily', operator: 'CP Alfa Pendular' },
    bus: { duration: '3h 30m', distance: '313 km', price: '€15-25', frequency: 'Hourly', operator: 'Rede Expressos' },
    car: { duration: '3h', distance: '313 km', price: '€35-50', tolls: '€23.70', fuel: '€25-35' }
  },
  'porto-lisbon': {
    train: { duration: '2h 52m', distance: '313 km', price: '€25-65', frequency: '15+ daily', operator: 'CP Alfa Pendular' },
    bus: { duration: '3h 30m', distance: '313 km', price: '€15-25', frequency: 'Hourly', operator: 'Rede Expressos' },
    car: { duration: '3h', distance: '313 km', price: '€35-50', tolls: '€23.70', fuel: '€25-35' }
  },
  
  // Lisbon to/from Sintra
  'lisbon-sintra': {
    train: { duration: '40m', distance: '32 km', price: '€2.30-4.50', frequency: 'Every 15-30 min', operator: 'CP' },
    bus: { duration: '45m', distance: '32 km', price: '€4.50-6', frequency: 'Every 30 min', operator: 'Scotturb' },
    car: { duration: '30m', distance: '32 km', price: '€8-15', tolls: '€1.80', fuel: '€3-5' }
  },
  'sintra-lisbon': {
    train: { duration: '40m', distance: '32 km', price: '€2.30-4.50', frequency: 'Every 15-30 min', operator: 'CP' },
    bus: { duration: '45m', distance: '32 km', price: '€4.50-6', frequency: 'Every 30 min', operator: 'Scotturb' },
    car: { duration: '30m', distance: '32 km', price: '€8-15', tolls: '€1.80', fuel: '€3-5' }
  },
  
  // Lisbon to/from Algarve (Faro)
  'lisbon-algarve': {
    train: { duration: '3h 15m', distance: '278 km', price: '€22.50-35', frequency: '5 daily', operator: 'CP Alfa Pendular' },
    bus: { duration: '3h 45m', distance: '278 km', price: '€20-30', frequency: '8 daily', operator: 'Rede Expressos' },
    car: { duration: '2h 45m', distance: '278 km', price: '€35-50', tolls: '€26.50', fuel: '€25-35' }
  },
  'algarve-lisbon': {
    train: { duration: '3h 15m', distance: '278 km', price: '€22.50-35', frequency: '5 daily', operator: 'CP Alfa Pendular' },
    bus: { duration: '3h 45m', distance: '278 km', price: '€20-30', frequency: '8 daily', operator: 'Rede Expressos' },
    car: { duration: '2h 45m', distance: '278 km', price: '€35-50', tolls: '€26.50', fuel: '€25-35' }
  },
  
  // Porto to/from Douro Valley (Peso da Régua)
  'porto-douro': {
    train: { duration: '2h 5m', distance: '125 km', price: '€12-20', frequency: '6 daily', operator: 'CP Regional' },
    bus: { duration: '2h 30m', distance: '125 km', price: '€10-15', frequency: '3 daily', operator: 'Rodonorte' },
    car: { duration: '1h 45m', distance: '125 km', price: '€20-30', tolls: '€12.30', fuel: '€12-18' }
  },
  'douro-porto': {
    train: { duration: '2h 5m', distance: '125 km', price: '€12-20', frequency: '6 daily', operator: 'CP Regional' },
    bus: { duration: '2h 30m', distance: '125 km', price: '€10-15', frequency: '3 daily', operator: 'Rodonorte' },
    car: { duration: '1h 45m', distance: '125 km', price: '€20-30', tolls: '€12.30', fuel: '€12-18' }
  },
  
  // Lisbon to/from Coimbra
  'lisbon-coimbra': {
    train: { duration: '1h 45m', distance: '205 km', price: '€15-30', frequency: '12 daily', operator: 'CP Intercidades' },
    bus: { duration: '2h 15m', distance: '205 km', price: '€12-18', frequency: '10 daily', operator: 'Rede Expressos' },
    car: { duration: '2h', distance: '205 km', price: '€25-40', tolls: '€18.90', fuel: '€18-25' }
  },
  'coimbra-lisbon': {
    train: { duration: '1h 45m', distance: '205 km', price: '€15-30', frequency: '12 daily', operator: 'CP Intercidades' },
    bus: { duration: '2h 15m', distance: '205 km', price: '€12-18', frequency: '10 daily', operator: 'Rede Expressos' },
    car: { duration: '2h', distance: '205 km', price: '€25-40', tolls: '€18.90', fuel: '€18-25' }
  },
  
  // Porto to/from Coimbra
  'porto-coimbra': {
    train: { duration: '1h 15m', distance: '120 km', price: '€12-20', frequency: '12 daily', operator: 'CP Alfa Pendular' },
    bus: { duration: '1h 30m', distance: '120 km', price: '€8-12', frequency: '8 daily', operator: 'Rede Expressos' },
    car: { duration: '1h 15m', distance: '120 km', price: '€15-25', tolls: '€9.50', fuel: '€10-15' }
  },
  'coimbra-porto': {
    train: { duration: '1h 15m', distance: '120 km', price: '€12-20', frequency: '12 daily', operator: 'CP Alfa Pendular' },
    bus: { duration: '1h 30m', distance: '120 km', price: '€8-12', frequency: '8 daily', operator: 'Rede Expressos' },
    car: { duration: '1h 15m', distance: '120 km', price: '€15-25', tolls: '€9.50', fuel: '€10-15' }
  },
  
  // Add more routes as needed
};

// Day accent colors and helper to pick by index - Portuguese color scheme
const DAY_COLORS = ["#E31B23", "#006E33", "#FFD700", "#1A4B8C", "#8E44AD", "#5D3A9B"];
function getColorForDay(idx) {
  return DAY_COLORS[idx % DAY_COLORS.length];
}

const TimelineWrapper = styled.div`
  /* Flexible container for itinerary columns */
  width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid ${props => props.color};
  background: linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%);
  margin-bottom: 1rem;
  
  /* Ensure proper flex behavior */
  flex: 1 0 auto;
  
  @media (min-width: 768px) {
    flex: 1 1 300px;
    min-width: 300px;
    margin: 0 1rem 1.5rem;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
    border-radius: 10px;
  }
  
  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    transform: translateY(-3px);
    border-left: 4px solid ${props => props.color};
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
    }
  }
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0.75rem 0.75rem 1.5rem;
  gap: 1.25rem;
  box-sizing: border-box;
  min-height: calc(100vh - 64px); /* Account for bottom nav */
  
  @media (min-width: 768px) {
    padding: 1.5rem;
    gap: 2rem;
    max-width: 1400px;
    margin: 0 auto;
    min-height: calc(100vh - 70px);
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 0.5rem 1.25rem;
    gap: 1rem;
  }
`;

const TemplateSelect = styled.select`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid ${colors.border};
  font-size: 0.95rem;
  background: #fff;
  color: ${colors.text};
  margin-left: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px ${colors.primary}20;
  }
  
  option:disabled {
    color: ${colors.muted};
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0.5rem;
    width: 100%;
  }
`;

const TitleInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid ${colors.border};
  font-size: 1rem;
  background: #fff;
  color: ${colors.text};
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: ${colors.muted};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px ${colors.primary}20;
  }
  
  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const InfoBox = styled.div`
  background-color: ${colors.background};
  border-left: 3px solid ${colors.primary};
  border-radius: 4px;
  padding: 1rem;
  margin: 1rem 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${colors.text};
  
  p {
    margin: 0 0 0.5rem 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    color: ${colors.primary};
    font-weight: 600;
  }
  
  code {
    background: rgba(0, 0, 0, 0.05);
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
  }
`;

const DescInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid ${colors.border};
  font-size: 0.95rem;
  font-family: inherit;
  line-height: 1.5;
  background: #fff;
  color: ${colors.text};
  margin-bottom: 0.5rem;
  resize: vertical;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: ${colors.muted};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px ${colors.primary}20;
  }
  
  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    min-height: 100px;
    font-size: 1rem;
  }
`;

const ActivitySelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid ${colors.border};
  font-size: 1rem;
  background: #fff;
  color: ${colors.text};
  margin-top: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px ${colors.primary}20;
  }
  
  &::placeholder {
    color: ${colors.muted};
    opacity: 0.8;
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  option {
    padding: 0.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  min-width: 0; /* Prevent flex item from overflowing */
  
  > *:first-child {
    margin-top: 0;
  }
  
  > *:last-child {
    margin-bottom: 0;
  }
`;

const RightColumn = styled.div`
  flex: 0 0 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  order: -1;
  
  @media (min-width: 992px) {
    flex: 0 0 360px;
    max-width: 360px;
    margin-left: 24px;
    order: 1;
  }
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid ${colors.border};
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 1.5rem 0 0 0;
  }
`;

const ExpandBtn = styled.button`
  background: transparent;
  border: none;
  color: ${colors.primary};
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 0.5rem;
  padding: 0.5rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(10, 132, 255, 0.1);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(10, 132, 255, 0.2);
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const ToggleButton = styled.button`
  background: ${props => props.$active ? colors.primary : 'transparent'};
  border: 1px solid ${props => props.$active ? colors.primary : colors.border};
  color: ${props => props.$active ? '#fff' : colors.text};
  padding: 0.5rem 1rem;
  margin: 0 0.3rem 0.5rem 0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? colors.primary : 'rgba(0, 0, 0, 0.05)'};
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(10, 132, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }
`;

const DayHeader = styled.div`
  background: ${props => props.color ? `${props.color}10` : 'rgba(10, 132, 255, 0.1)'};
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  
  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: ${props => props.color || colors.primary};
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .day-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    
    .day-actions {
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
    }
  }
`;

const DayRow = styled.div`
  display: flex;
  margin-bottom: 2rem;
  position: relative;
  gap: 1.5rem;
  align-items: flex-start;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const TransportWrapper = styled.div`
  width: 100%;
  margin: 1rem 0;
  padding: 0 1rem;
  box-sizing: border-box;
`;

const RightInfo = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 0.95rem;
  color: ${colors.muted};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${colors.muted};
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem 0 0 0;
    width: 100%;
    justify-content: flex-end;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${colors.primary};
  word-break: break-word;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${colors.border};
  
  svg {
    color: ${colors.primary};
    width: 20px;
    height: 20px;
  }
  
  @media (prefers-color-scheme: dark) {
    color: #5e9fff;
    border-color: #3a3a3c;
    
    svg {
      color: #5e9fff;
    }
  }
`;

const SubTitle = styled.div`
  margin: 1rem 0 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.text};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  word-break: break-word;
  
  svg {
    color: ${colors.primary};
    width: 16px;
    height: 16px;
  }
  
  @media (prefers-color-scheme: dark) {
    color: #fff;
    
    svg {
      color: #5e9fff;
    }
  }
`;

const CitySelect = styled.select`
  padding: 0.8rem 2.5rem 0.8rem 1.2rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  font-size: 1rem;
  background-color: #f8fafc;
  color: #1e293b;
  width: 100%;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%234b5563'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.5em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  
  &:focus {
    outline: none;
    border-color: #E31B23;
    box-shadow: 0 0 0 3px rgba(227, 27, 35, 0.15);
    background-color: #fff;
  }
  background: #fff;
  color: ${colors.text};
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9L12 15L18 9' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;
  cursor: pointer;
  
  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(10, 132, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 0.5rem;
  }
  
  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    background-color: #2c2c2e;
    border-color: #3a3a3c;
    
    option {
      background: #2c2c2e;
      color: #fff;
    }
  }
`;

const Slot = styled.div`
  background: #fff;
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin: 0 1.5rem 1.5rem;
  padding: 1.5rem;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.color || colors.primary};
  }
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    margin: 0 1rem 1.25rem;
    padding: 1.25rem 1rem 1.25rem 1.25rem;
  }
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
  }
`;

const SlotLabel = styled.div`
  font-size: 1.03rem;
  font-weight: 600;
  color: ${colors.primary};
  margin-bottom: 0.7rem;
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${colors.border};
  background: linear-gradient(90deg, rgba(227, 27, 35, 0.1), rgba(0, 110, 51, 0.1));
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(to bottom, #E31B23, #FFD700, #006E33);
  }
  
  h4 {
    margin: 0;
    font-size: 1.1rem;
    color: ${colors.primary};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  @media (prefers-color-scheme: dark) {
    background: #2c2c2e;
    border-color: #3a3a3c;
    
    h4 {
      color: #5e9fff;
    }
  }
`;

const ActivityContent = styled.div`
  padding: 1.5rem;
  background: #fff;
  
  > *:last-child {
    margin-bottom: 0;
  }
  
  @media (prefers-color-scheme: dark) {
    background: #1c1c1e;
    color: #e5e5ea;
  }
`;

const ActivityCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 6px;
  padding: 0.6rem 0.8rem 0.6rem 0.9rem;
  margin-bottom: 0.5rem;
  cursor: grab;
  padding-top: 1.5rem;
  border-left: 4px solid 
    ${props => {
      if (!props.$hasReservation) return 'transparent';
      switch(props.$reservationStatus) {
        case 'confirmed': return '#10B981';
        case 'pending': return '#F59E0B';
        case 'cancelled': return '#EF4444';
        default: return 'transparent';
      }
    }};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid #e9ecef;
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: #dee2e6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 0;
    bottom: 6px;
    width: 2px;
    background: #E31B23;
    border-radius: 1px;
  }
  
  .activity-actions {
    display: flex;
    gap: 0.6rem;
    margin-top: 0.8rem;
    flex-wrap: wrap;
    
    @media (max-width: 768px) {
      gap: 0.8rem;
      margin-top: 1rem;
    }
  }
  
  .details-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: #fff;
    border: 1px solid #1a73e8;
    border-radius: 0.5rem;
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #1a73e8;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    user-select: none;
    transition: all 0.2s ease;
    text-decoration: none;
    min-width: 100px;
    position: relative;
    min-height: 44px; /* Minimum touch target size for mobile */
    touch-action: manipulation; /* Improve touch response */
    
    &:hover {
      background: #1a73e8;
      color: #fff;
    }
    
    &:active {
      transform: translateY(0);
    }
    
    svg {
      width: 16px;
      height: 16px;
      color: inherit;
      transition: color 0.2s ease;
      flex-shrink: 0;
    }
  }
  
  .activity-desc {
    font-size: 0.85rem;
    color: #6c757d;
    margin-top: 0.3rem;
    line-height: 1.4;
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem 0 0 0;
  }
`;

const ActivityMeta = styled.div`
  display: none; /* Hide the meta info to make cards more compact */
  
  @media (prefers-color-scheme: dark) {
    color: #8e8e93;
    border-color: #3a3a3c;
  }
`;

const Img = styled.img`
  width: 54px;
  height: 54px;
  object-fit: cover;
  border-radius: 0.5rem;
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  line-height: 1.3;
  
  .rating {
    font-size: 0.75rem;
    color: #718096;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    margin-left: 0.4rem;
    
    svg {
      color: #ecc94b;
    }
  }
`;

const Desc = styled.div`
  font-size: 0.85rem;
  color: #4a5568;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.5;
`;

const RemoveBtn = styled.button`
  margin-left: auto;
  background: #fff;
  color: ${colors.primary};
  border: 1px solid ${colors.primary};
  border-radius: 0.5rem;
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 36px;
  min-width: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${colors.primary};
    color: #fff;
  }
`;

const AddBtn = styled.button`
  background: ${colors.accent};
  color: #fff;
  border: none;
  border-radius: 0.7rem;
  font-size: 1.02rem;
  font-weight: 600;
  padding: 0.6rem 1.2rem;
  margin-top: 0.7rem;
  cursor: pointer;
  transition: background 0.13s;
  &:hover { background: ${colors.primary}; }
`;

const DeleteDayBtn = styled.button`
  margin-left: auto;
  background: #fff;
  color: ${colors.primary};
  border: 1px solid ${colors.primary};
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.98rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.13s, color 0.13s;
  &:hover {
  }
  
  @media (max-width: 768px) {
    width: ${props => props.fullWidth ? '100%' : 'auto'};
    padding: ${props => props.size === 'small' ? '0.5rem' : '0.8rem 1rem'};
  }
`;

const OptimizeBtn = styled(Button)`
  margin-left: 0.8rem;
  background: ${colors.primary};
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.98rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.13s;
  &:hover { background: ${colors.accent}; }
`;

const MapBtn = styled(Button)`
  background: ${colors.primary};
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(10, 132, 255, 0.3);
  
  &:hover {
    background: ${colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 132, 255, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin: 0.5rem 0 0 0;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1.2rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  margin-bottom: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: #f8fafc;
  color: #1e293b;
  font-weight: 400;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &::placeholder {
    color: #94a3b8;
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: #E31B23;
    box-shadow: 0 0 0 3px rgba(227, 27, 35, 0.15);
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(10, 132, 255, 0.2);
  }
  
  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
  
  @media (prefers-color-scheme: dark) {
    background: #2c2c2e;
    border-color: #3a3a3c;
    
    &::placeholder {
      color: #8e8e93;
    }
    
    &:disabled {
      background: #1c1c1e;
      color: #8e8e93;
    }
  }
`;

const DeleteButton = styled(Button)`
  background: #ff3b30;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(255, 59, 48, 0.3);
  transition: all 0.2s ease;
  
  &:hover {
    background: #ff2d1a;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 59, 48, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    margin-right: 0.5rem;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin: 0.5rem 0 0 0;
  }
`;

const DateLabel = styled.span`
  font-size: 0.9rem;
  color: ${colors.muted};
  margin-left: 0.5rem;
`;

const DateInput = styled.input`
  font-size: 0.9rem;
  color: ${colors.text};
  border: 1px solid ${colors.border};
  border-radius: 0.3rem;
  padding: 0.2rem 0.5rem;
  margin-left: 0.5rem;
`;

const StyledMapContainer = styled.div`
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  margin: 1.5rem 0;
  border: 1px solid ${colors.border};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
  
  .leaflet-container {
    width: 100%;
    height: 100%;
    background: #f8f9fa;
  }
  
  .leaflet-popup-content-wrapper {
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    padding: 0.5rem;
  }
  
  .leaflet-popup-content {
    margin: 0.5rem;
    font-size: 0.95rem;
    line-height: 1.4;
  }
  
  .leaflet-popup-tip {
    box-shadow: none;
  }
  
  @media (prefers-color-scheme: dark) {
    border-color: #3a3a3c;
    
    .leaflet-container {
      background: #1c1c1e;
    }
    
    .leaflet-popup-content-wrapper {
      background: #2c2c2e;
      color: #fff;
    }
    
    .leaflet-popup-tip {
      background: #2c2c2e;
    }
  }
`;

const TIME_SLOTS = [
  { key: "morning", label: "Morning" },
  { key: "lunch", label: "Lunch" },
  { key: "afternoon", label: "Afternoon" },
  { key: "dinner", label: "Dinner" },
  { key: "evening", label: "Evening" }
];

const CITIES = ["Lisbon", "Porto", "Faro", "Coimbra", "Évora", "Braga", "Sintra", "Aveiro", "Albufeira", "Lagos", "Sagres", "Douro"];



const CITY_COORDS = {
  Lisbon: { lat: 38.7223, lon: -9.1393 },
  Porto: { lat: 41.1496, lon: -8.6109 },
  Faro: { lat: 37.0194, lon: -7.9304 },
  Coimbra: { lat: 40.2033, lon: -8.4103 },
  Évora: { lat: 38.5667, lon: -7.9 },
  Braga: { lat: 41.5454, lon: -8.4265 },
  Sintra: { lat: 38.8029, lon: -9.3817 },
  Aveiro: { lat: 40.6405, lon: -8.6538 },
};







// Helper function outside component
const migrateDays = (days) => {
  return days.map(day => {
    if (day.slots && day.city) return day;
    return {
      ...day,
      city: day.city || CITIES[0],
      slots: day.slots || { morning: day.items || [], lunch: [], afternoon: [], dinner: [], evening: [] }
    };
  });
};

// Reservation Status Badge Component
const ReservationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  margin-left: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &.status-confirmed {
    background-color: #dcfce7;
    color: #166534;
    border: 1px solid #bbf7d0;
  }

  &.status-pending {
    background-color: #fef9c3;
    color: #854d0e;
    border: 1px solid #fef08a;
  }

  &.status-cancelled {
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  &.status-not_needed {
    background-color: #f3f4f6;
    color: #4b5563;
    border: 1px solid #e5e7eb;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

// Reservation Button with Status
const ReserveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  z-index: 1;
  
  &:hover {
    background: #1557b0;
  }
`;

const ReservationButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: ${props => {
    if (!props.$status) return 'white';
    switch(props.$status) {
      case 'confirmed': return '#dcfce7';
      case 'pending': return '#fef9c3';
      case 'cancelled': return '#fee2e2';
      default: return 'white';
    }
  }};
  border: 1px solid ${props => {
    if (!props.$status) return colors.primary;
    switch(props.$status) {
      case 'confirmed': return '#bbf7d0';
      case 'pending': return '#fef08a';
      case 'cancelled': return '#fecaca';
      default: return colors.primary;
    }
  }};
  color: ${props => {
    if (!props.$status) return colors.primary;
    switch(props.$status) {
      case 'confirmed': return '#166534';
      case 'pending': return '#854d0e';
      case 'cancelled': return '#991b1b';
      default: return colors.primary;
    }
  }};
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  z-index: 1;

  &:hover {
    background-color: ${props => {
      if (!props.$status) return colors.primary;
      switch(props.$status) {
        case 'confirmed': return '#bbf7d0';
        case 'pending': return '#fef08a';
        case 'cancelled': return '#fecaca';
        default: return colors.primary;
      }
    }};
    color: ${props => {
      if (!props.$status) return 'white';
      switch(props.$status) {
        case 'confirmed': return '#166534';
        case 'pending': return '#854d0e';
        case 'cancelled': return '#991b1b';
        default: return 'white';
      }
    }};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 0.8rem;
    height: 0.8rem;
  }
`;

// Reservation Modal
const ReservationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1.5rem;
  font-size: 1.25rem;
  color: #1f2937;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
  }

  select,
  input,
  textarea {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }
  }

  textarea {
    min-height: 80px;
    resize: vertical;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const ModalButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background-color: #4f46e5;
    color: white;
    border: 1px solid #4f46e5;

    &:hover {
      background-color: #4338ca;
    }
  }

  &.secondary {
    background-color: white;
    color: #4b5563;
    border: 1px solid #d1d5db;

    &:hover {
      background-color: #f9fafb;
    }
  }
`;

const PrivateTourBadge = styled.span`
  background: #4f46e5;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  margin-left: 0.5rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
`;

export default function TimelinePlanner({ days, setDays, activitiesByCity, startDate }) {
  // State for reservation modal
  const [reservationModal, setReservationModal] = React.useState({
    isOpen: false,
    dayIndex: null,
    slotKey: '',
    activityIndex: null,
    activity: null,
    isSending: false,
    error: null,
    success: false
  });

  // Initialize timelineDays from props
  const [timelineDays, setTimelineDays] = React.useState(() => migrateDays(days));

  // Handle opening the reservation modal
  const handleOpenReservationModal = (dayIndex, slotKey, activityIndex, activity) => {
    // Only open if we have valid parameters
    if (dayIndex === null || slotKey === null || activityIndex === null || !activity) {
      return;
    }
    
    const isRestaurantActivity = isRestaurant(activity);
    
    setReservationModal({
      isOpen: true,
      dayIndex,
      slotKey,
      activityIndex,
      isSending: false,
      error: null,
      success: false,
      activity: {
        ...activity,
        isRestaurant: isRestaurantActivity,
        reservation: activity.reservation || {
          status: isRestaurantActivity ? 'pending' : 'not_needed',
          bookingRef: '',
          notes: '',
          partySize: 2,
          required: isRestaurantActivity,
          type: isRestaurantActivity ? 'restaurant' : 'other'
        }
      }
    });
  };

  // Handle closing the reservation modal
  const handleCloseReservationModal = () => {
    setReservationModal({
      isOpen: false,
      dayIndex: null,
      slotKey: '',
      activityIndex: null,
      activity: null
    });
  };

  // Handle changes in the reservation form
  const handleReservationChange = (field, value) => {
    setReservationModal(prev => ({
      ...prev,
      activity: {
        ...prev.activity,
        reservation: {
          ...prev.activity.reservation,
          [field]: value
        }
      }
    }));
  };

  // Save the reservation and update the activity
  const saveReservation = () => {
    const { dayIndex, slotKey, activityIndex, activity } = reservationModal;
    
    if (dayIndex === null || !slotKey || activityIndex === null) {
      handleCloseReservationModal();
      return;
    }

    const updatedDays = [...timelineDays];
    const updatedSlots = [...updatedDays[dayIndex].slots[slotKey]];
    
    // Update the activity with the reservation details
    updatedSlots[activityIndex] = {
      ...updatedSlots[activityIndex],
      reservation: {
        ...activity.reservation,
        // Only include reservation object if status is not 'not_needed' or if there are other details
        ...(activity.reservation.status === 'not_needed' && 
            !activity.reservation.bookingRef && 
            !activity.reservation.notes && 
            !activity.reservation.required 
              ? null 
              : { ...activity.reservation })
      }
    };

    // Remove reservation object if it's empty or not needed
    if (activity.reservation.status === 'not_needed' && 
        !activity.reservation.bookingRef && 
        !activity.reservation.notes && 
        !activity.reservation.required) {
      delete updatedSlots[activityIndex].reservation;
    }

    // Update the day's slots
    updatedDays[dayIndex] = {
      ...updatedDays[dayIndex],
      slots: {
        ...updatedDays[dayIndex].slots,
        [slotKey]: updatedSlots
      }
    };

    // Update the state
    setTimelineDays(updatedDays);
    setDays(updatedDays);
    handleCloseReservationModal();
  };

  // Render the reservation status badge
  const renderReservationBadge = (reservation) => {
    if (!reservation || reservation.status === 'not_needed') return null;
    
    const statusText = {
      confirmed: 'Confirmed',
      pending: 'Pending',
      cancelled: 'Cancelled',
      not_needed: 'Not Needed'
    }[reservation.status] || reservation.status;
    
    return (
      <ReservationBadge className={`status-${reservation.status}`}>
        {reservation.status === 'confirmed' && <FiCheck size={12} />}
        {reservation.status === 'pending' && <FiAlertCircle size={12} />}
        {reservation.status === 'cancelled' && <FiX size={12} />}
        {statusText}
      </ReservationBadge>
    );
  };

  const [expandedDays, setExpandedDays] = React.useState(() => timelineDays.map(() => false));
  const [dayDates, setDayDates] = React.useState([]);
  const [mapKey, setMapKey] = React.useState(0);
  const [selectedPoi, setSelectedPoi] = React.useState(null);

  // State declarations at the top
  // All hooks must be called in the same order on every render
  const toggleExpand = React.useCallback((idx) => {
    setExpandedDays(prev => {
      const newExpanded = [...prev];
      newExpanded[idx] = !newExpanded[idx];
      return newExpanded;
    });
  }, []);

  // Initialize dates and expanded days when timeline changes
  React.useEffect(() => {
    if (startDate) {
      const base = new Date(startDate);
      const dates = timelineDays.map((_, idx) => {
        const d = new Date(base);
        d.setDate(d.getDate() + idx);
        return d.toISOString().slice(0,10);
      });
      setDayDates(dates);
    }
    
    // Reset expanded days when timeline length changes
    setExpandedDays(prev => {
      if (prev.length !== timelineDays.length) {
        return timelineDays.map(() => false);
      }
      return prev;
    });
  }, [startDate, timelineDays.length]);

  // Prepare route points and compute distance/time
  const routePoints = React.useMemo(() => {
    const pts = [];
    timelineDays.forEach(day => {
      Object.values(day.slots).forEach(items => items.forEach(item => {
        if (item.lat != null && item.lng != null) pts.push([item.lat, item.lng]);
      }));
    });
    return pts;
  }, [timelineDays]);
  const totalDist = routePoints.reduce((sum, curr, idx, arr) =>
    idx > 0 ? sum + getDistance({ lat: arr[idx-1][0], lng: arr[idx-1][1] }, { lat: curr[0], lng: curr[1] }) : sum
  , 0);
  const totalTimeMin = Math.round((totalDist / 5) * 60);

  // Get transport options between two cities
  const getTransportOptions = (fromCity, toCity) => {
    if (!fromCity || !toCity) return null;
    
    const from = fromCity.toLowerCase();
    const to = toCity.toLowerCase();
    const routeKey = `${from}-${to}`;
    
    const defaultTransport = {
      train: { duration: 'Varies', distance: 'Varies', price: 'Varies' },
      bus: { duration: 'Varies', distance: 'Varies', price: 'Varies' },
      car: { duration: 'Varies', distance: 'Varies', price: 'Varies' }
    };
    
    return {
      ...defaultTransport,
      ...(TRANSPORT_OPTIONS[routeKey] || {})
    };
  };

  // Calculate distance and duration between two cities
  const calculateRoute = (fromCity, toCity, transportType = 'train') => {
    if (!fromCity || !toCity || fromCity === toCity) return null;
    
    const from = CITY_COORDINATES[fromCity.toLowerCase()];
    const to = CITY_COORDINATES[toCity.toLowerCase()];
    
    const key = `${fromCity.toLowerCase()}-${toCity.toLowerCase()}`;
    const reverseKey = `${toCity.toLowerCase()}-${fromCity.toLowerCase()}`;
    
    // Try both directions since transport options are the same in both directions
    const options = TRANSPORT_OPTIONS[key] || TRANSPORT_OPTIONS[reverseKey];
    
    if (!options) {
      console.warn(`No transport options found for ${fromCity} to ${toCity}`);
      return {
        type: 'car',
        duration: 'Varies',
        distance: 'Varies',
        price: 'Varies',
        frequency: 'On demand',
        operator: 'Various',
        tolls: 'Varies',
        fuel: 'Varies',
        from: fromCity,
        to: toCity,
        fromCoords: from ? [from.lon, from.lat] : [0, 0],
        toCoords: to ? [to.lon, to.lat] : [0, 0]
      };
    }
    
    // Return the first available transport type (train, bus, or car)
    const selectedType = options.train ? 'train' : options.bus ? 'bus' : 'car';
    const transportInfo = options[selectedType] || {};
    
    return {
      type: selectedType,
      duration: transportInfo.duration || 'Unknown',
      distance: transportInfo.distance || 'Unknown',
      price: transportInfo.price || 'Varies',
      frequency: transportInfo.frequency || 'Multiple daily',
      operator: transportInfo.operator || 'Various',
      tolls: transportInfo.tolls || 'N/A',
      fuel: transportInfo.fuel || 'N/A',
      from: fromCity,
      to: toCity,
      fromCoords: from ? [from.lon, from.lat] : [0, 0],
      toCoords: to ? [to.lon, to.lat] : [0, 0]
    };
  };

  // Save to parent
  const updateDay = (dIdx, newSlots) => {
    const newDays = timelineDays.map((d, idx) => idx === dIdx ? { ...d, slots: newSlots } : d);
    setTimelineDays(newDays);
    setDays(newDays);
  };

  const removeActivity = (dIdx, slotKey, iIdx) => {
    const newSlots = { ...timelineDays[dIdx].slots };
    newSlots[slotKey] = newSlots[slotKey].filter((_, j) => j !== iIdx);
    updateDay(dIdx, newSlots);
  };

  // Remove a day
  const removeDay = (dIdx) => {
    const newDays = timelineDays.filter((_, idx) => idx !== dIdx);
    setTimelineDays(newDays);
    setDays(newDays);
  };

  // Update a field of an activity
  const updateActivity = (dIdx, slotKey, iIdx, field, value) => {
    const slots = { ...timelineDays[dIdx].slots };
    const items = [...slots[slotKey]];
    items[iIdx] = { ...items[iIdx], [field]: value };
    slots[slotKey] = items;
    updateDay(dIdx, slots);
  };

  // For demo: add empty activity
  const addActivity = (dIdx, slotKey) => {
    const newSlots = { ...timelineDays[dIdx].slots };
    newSlots[slotKey] = [...newSlots[slotKey], { 
      title: "New Activity",
      desc: "Add a description...",
      image: ""
    }];
    updateDay(dIdx, newSlots);
  };

  // Add a new day with empty slots
  const addDay = () => {
    const newDay = {
      label: `Day ${timelineDays.length + 1}`,
      slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] }
    };
    const newTimelineDays = [...timelineDays, newDay];
    setTimelineDays(newTimelineDays);
    setDays(newTimelineDays);
  };

  // Cross-day drag-and-drop
  const handleDragEndCross = (result) => {
  if (!result.destination) return;
  const [srcDayIdx, srcSlot] = result.source.droppableId.split("-");
  const [destDayIdx, destSlot] = result.destination.droppableId.split("-");
  const srcIndex = result.source.index;
  const destIndex = result.destination.index;
  const newTimeline = [...timelineDays];
  // remove from source
  const srcItems = Array.from(newTimeline[srcDayIdx].slots[srcSlot]);
  const [moved] = srcItems.splice(srcIndex, 1);
  // Check validSlots for destination
  const activities = activitiesByCity[newTimeline[destDayIdx].city] || [];
  const activity = activities.find(a => a.title === moved.title);
  if (activity && activity.validSlots && !activity.validSlots.includes(destSlot)) {
    alert('This activity is not available for this time slot.');
    return;
  }
  newTimeline[srcDayIdx].slots[srcSlot] = srcItems;
  // insert into destination
  const destItems = Array.from(newTimeline[destDayIdx].slots[destSlot]);
  destItems.splice(destIndex, 0, moved);
  newTimeline[destDayIdx].slots[destSlot] = destItems;
  setTimelineDays(newTimeline);
  setDays(newTimeline);
};

  const updateCity = (dIdx, city) => {
    const newDays = timelineDays.map((d, idx) => idx === dIdx ? { ...d, city } : d);
    setTimelineDays(newDays);
    setDays(newDays);
    // Force remount of the map by updating the key
    setMapKey(prevKey => prevKey + 1);
  };

  // Open Google Maps with route for the day's POIs
  const openMaps = (dayIndex) => {
    const day = timelineDays[dayIndex];
    
    // Get all POIs with valid coordinates for the day
    const pois = Object.values(day.slots)
      .flat()
      .filter(item => {
        // Check if coordinates are valid numbers
        const lat = Number(item.lat);
        const lng = Number(item.lng);
        return !isNaN(lat) && !isNaN(lng) && 
               lat !== 0 && lng !== 0 && 
               Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
      })
      .map(item => {
        // Prepare photos array with proper structure
        const photos = [];
        
        // Handle different photo formats
        if (item.photos) {
          if (Array.isArray(item.photos)) {
            // If photos is an array, map to our format
            photos.push(...item.photos.map(photo => ({
              url: photo.getUrl ? photo.getUrl() : (photo.url || photo),
              source: 'google_places',
              attribution: 'Google Places'
            })));
          } else if (typeof item.photos === 'string') {
            // If it's a single URL string
            photos.push({
              url: item.photos,
              source: 'custom',
              attribution: ''
            });
          }
        }
        
        // Prepare types array
        const types = [];
        if (item.types && Array.isArray(item.types)) {
          types.push(...item.types);
        } else if (item.type) {
          types.push(item.type);
        }
        if (types.length === 0) types.push('point_of_interest');
        
        // Prepare opening hours
        let opening_hours = {
          open_now: false,
          weekday_text: []
        };
        
        if (item.opening_hours) {
          if (item.opening_hours.weekday_text) {
            opening_hours.weekday_text = item.opening_hours.weekday_text;
          }
          if (item.opening_hours.open_now !== undefined) {
            opening_hours.open_now = item.opening_hours.open_now;
          }
        }
        
        // Return the formatted item
        return {
          ...item,
          id: item.id || `poi-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: item.title || item.name || 'Unnamed Location',
          description: item.desc || item.description || '',
          types: [...new Set(types)],
          photos: photos.length > 0 ? photos : null,
          rating: typeof item.rating === 'number' ? item.rating : 0,
          user_ratings_total: typeof item.user_ratings_total === 'number' ? item.user_ratings_total : 0,
          price_level: typeof item.price_level === 'number' ? item.price_level : 0,
          opening_hours,
          vicinity: item.vicinity || item.address || item.desc || 'Location',
          website: item.website || '',
          phone: item.phone || '',
          // Ensure lat/lng are numbers
          lat: Number(item.lat),
          lng: Number(item.lng),
          // Preserve original data
          originalData: item
        };
      });
    
    return (
      <div key={`map-container-${dayIndex}`} style={{
        margin: '1rem 0',
        height: '400px',
        position: 'relative',
        width: '100%',
        minHeight: '400px',
        backgroundColor: '#f0f0f0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <MapComponent 
          key={`map-${dayIndex}-${mapKey}`}
          items={pois}
          center={pois[0] ? [pois[0].lng, pois[0].lat] : undefined}
          zoom={pois.length > 1 ? 12 : 14}
          onMarkerClick={(item) => setSelectedPoi(item)}
          showDetailedInfo={true}
        />
      </div>
    );
  };

  // Add day template for a city
  const addDayTemplate = (city) => {
    const templates = dayTemplatesByCity[city] || [];
    if (!templates.length) return;
    const newTimelineDays = [...timelineDays, ...templates.map(t => ({ ...t, label: `Day ${timelineDays.length + 1}`, city }))];
    setTimelineDays(newTimelineDays);
    setDays(newTimelineDays);
  };

  // Apply a template to an existing day
  const applyTemplate = (dIdx, templateIdx) => {
    const city = timelineDays[dIdx].city;
    const templates = dayTemplatesByCity[city] || [];
    const tmpl = templates[templateIdx];
    if (!tmpl) return;
    
    const newDays = timelineDays.map((d, idx) => {
      if (idx !== dIdx) return d;
      
      if (tmpl.isPrivateTour) {
        const allDayItem = tmpl.slots.allDay?.[0] || {};
        return {
          ...d,
          ...allDayItem,
          id: d.id,
          city: d.city,
          slots: {
            allDay: [allDayItem],
            morning: [],
            lunch: [],
            afternoon: [],
            dinner: [],
            evening: []
          },
          isPrivateTour: true,
          label: tmpl.label
        };
      }
      
      return { 
        ...d, 
        slots: tmpl.slots,
        label: tmpl.label || d.label
      };
    });
    
    setTimelineDays(newDays);
    setDays(newDays);
    setMapKey(prevKey => prevKey + 1);
  };

  // Optimize activities for a day: minimize travel, fit valid slots
  const optimizeDay = (dIdx) => {
    const day = timelineDays[dIdx];
    const slots = Object.keys(day.slots);
    let activities = [];
    slots.forEach(slotKey => {
      (day.slots[slotKey] || []).forEach(item => {
        const act = (activitiesByCity[day.city] || []).find(a => a.title === item.title);
        if (act && act.validSlots && act.validSlots.includes(slotKey)) {
          activities.push({ ...item, slotKey });
        }
      });
    });
    // Simple greedy: start with morning, then pick next closest valid for next slot
    let ordered = [];
    let used = new Set();
    let prev = null;
    slots.forEach(slotKey => {
      let candidates = activities.filter(a => !used.has(a.title) && (!a.validSlots || a.validSlots.includes(slotKey)));
      if (candidates.length === 0) return;
      let next;
      if (!prev) {
        next = candidates[0];
      } else {
        next = candidates.reduce((min, curr) => {
          const d = getDistance(prev, curr);
          return d < getDistance(prev, min) ? curr : min;
        }, candidates[0]);
      }
      ordered.push({ ...next, slotKey });
      used.add(next.title);
      prev = next;
    });
    // Fill slots
    let newSlots = {};
    slots.forEach(slotKey => {
      newSlots[slotKey] = ordered.filter(a => a.slotKey === slotKey).map(a => ({ ...a }));
    });
    // Suggest fill for empty slots
    slots.forEach(slotKey => {
      if (!newSlots[slotKey] || newSlots[slotKey].length === 0) {
        // Find a valid activity not used yet
        const candidates = (activitiesByCity[day.city] || []).filter(a => (!used.has(a.title)) && (!a.validSlots || a.validSlots.includes(slotKey)));
        if (candidates.length > 0) {
          newSlots[slotKey] = [{ ...candidates[0] }];
          used.add(candidates[0].title);
        }
      }
    });
    const newTimeline = [...timelineDays];
    newTimeline[dIdx] = { ...day, slots: newSlots };
    setTimelineDays(newTimeline);
    setDays(newTimeline);
  };

  // Smart suggestions sidebar
  const getSuggestions = (day) => {
    let suggestions = [];
    const slots = Object.keys(day.slots);
    // Fill gaps
    slots.forEach(slotKey => {
      if (!day.slots[slotKey] || day.slots[slotKey].length === 0) {
        const candidates = (activitiesByCity[day.city] || []).filter(a => a.validSlots && a.validSlots.includes(slotKey));
        if (candidates.length > 0) {
          suggestions.push({ type: 'fill', slotKey, activity: candidates[0] });
        }
      }
    });
    // Warn about conflicts (duplicate activities)
    let seen = new Set();
    slots.forEach(slotKey => {
      (day.slots[slotKey] || []).forEach(item => {
        if (seen.has(item.title)) {
          suggestions.push({ type: 'conflict', slotKey, activity: item });
        }
        seen.add(item.title);
      });
    });
    // Suggest alternatives
    slots.forEach(slotKey => {
      (day.slots[slotKey] || []).forEach(item => {
        const alternatives = (activitiesByCity[day.city] || []).filter(a => a.title !== item.title && a.validSlots && a.validSlots.includes(slotKey));
        if (alternatives.length > 0) {
          suggestions.push({ type: 'alternative', slotKey, activity: item, alternatives });
        }
      });
    });
    return suggestions;
  };

  // Handle date changes - updates the selected date and all subsequent dates
  const handleDateChange = React.useCallback((idx, val) => {
    setDayDates(prev => {
      const base = new Date(val);
      return prev.map((date, i) => {
        if (i === idx) return val;
        if (i > idx) {
          const d = new Date(base);
          d.setDate(d.getDate() + (i - idx));
          return d.toISOString().slice(0, 10);
        }
        return date;
      });
    });
  }, []);

  // Initialize or update dates when timeline changes
  React.useEffect(() => {
    if (!startDate || !timelineDays.length) return;
    
    const base = new Date(startDate);
    const newDates = [];
    
    for (let i = 0; i < timelineDays.length; i++) {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      newDates.push(d.toISOString().slice(0, 10));
    }
    
    setDayDates(newDates);
  }, [startDate, timelineDays.length]);

  // Fetch weather for each selected date

  // Handle restaurant reservation via Google
  const handleRestaurantReservation = (restaurant, dayIndex, slotKey) => {
    // Get the date for this day
    const reservationDate = new Date(startDate);
    reservationDate.setDate(reservationDate.getDate() + dayIndex);
    
    // Get default time based on slot (lunch, dinner, etc.)
    const defaultTime = getDefaultReservationTime(slotKey);
    
    // Generate the reservation URL with all parameters
    const url = getGoogleReservationLink({
      restaurantName: restaurant.title,
      location: restaurant.location || '',
      date: reservationDate,
      time: defaultTime,
      partySize: 2 // Default party size, can be made configurable
    });
    
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Add the reservation modal
  const ReservationModalComponent = () => (
    reservationModal.isOpen && (
      <ReservationModal 
        isOpen={reservationModal.isOpen} 
        onClick={handleCloseReservationModal}
      >
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalTitle>
          {reservationModal.activity?.title || 'Activity'} Reservation
        </ModalTitle>
        
        <FormGroup>
          <label>Reservation Status</label>
          <select
            value={reservationModal.activity?.reservation?.status || 'not_needed'}
            onChange={(e) => handleReservationChange('status', e.target.value)}
          >
            <option value="not_needed">No Reservation Needed</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </FormGroup>

        {reservationModal.activity?.reservation?.status !== 'not_needed' && (
          <>
            <FormGroup>
              <label>Booking Reference</label>
              <input
                type="text"
                placeholder="e.g., RES-12345"
                value={reservationModal.activity?.reservation?.bookingRef || ''}
                onChange={(e) => handleReservationChange('bookingRef', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <label>Party Size</label>
              <input
                type="number"
                min="1"
                value={reservationModal.activity?.reservation?.partySize || 2}
                onChange={(e) => handleReservationChange('partySize', parseInt(e.target.value) || 2)}
              />
            </FormGroup>


            <FormGroup>
              <label>Notes</label>
              <textarea
                placeholder="Special requests or additional information"
                value={reservationModal.activity?.reservation?.notes || ''}
                onChange={(e) => handleReservationChange('notes', e.target.value)}
              />
            </FormGroup>

          </>
        )}

        <ModalActions>
          <ModalButton 
            className="secondary" 
            onClick={handleCloseReservationModal}
          >
            Cancel
          </ModalButton>
          <ModalButton 
            className="primary" 
            onClick={saveReservation}
          >
            Save Reservation
          </ModalButton>
        </ModalActions>
        </ModalContent>
      </ReservationModal>
    )
  );

  return (
    <React.Fragment>
      <ReservationModalComponent />
      <DragDropContext onDragEnd={handleDragEndCross}>
        <Main>
          <LeftColumn>
          {timelineDays.map((day, dIdx) => {
            const accent = getColorForDay(dIdx);
            const showTransport = dIdx > 0 && day.city !== timelineDays[dIdx - 1]?.city;
            return (
              <React.Fragment key={dIdx}>
                {/* Transport segment from previous day to current day */}
                {showTransport && (
                  <TransportWrapper>
                    <TransportSegment
                      from={timelineDays[dIdx - 1]?.city}
                      to={day.city}
                      {...calculateRoute(timelineDays[dIdx - 1]?.city, day.city)}
                    />
                  </TransportWrapper>
                )}
                <DayRow>
                  <TimelineWrapper color={accent}>
                  {dIdx === 0 ? (
                    <div style={{ margin: '0.5rem 0', display: 'flex', alignItems: 'center' }}>
                      <label style={{ marginRight: '0.5rem' }}>Date:</label>
                      <DateInput
                        type="date"
                        value={dayDates[0] || ''}
                        onChange={e => handleDateChange(0, e.target.value)}
                      />
                    </div>
                  ) : dayDates[0] && (
                    <div style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                      <DateLabel>
                        {(() => {
                          const dt = new Date(dayDates[0]);
                          dt.setDate(dt.getDate() + dIdx);
                          return `${dt.getDate()}/${dt.getMonth() + 1}`;
                        })()}
                      </DateLabel>
                    </div>
                  )}
                  <DayHeader>
                    <ExpandBtn onClick={() => toggleExpand(dIdx)}>
                      {expandedDays[dIdx] ? '▲ Hide Details' : '▼ Show Details'}
                    </ExpandBtn>
                    <span style={{ marginRight: 8, fontWeight: 600 }}>{day.label}</span>
                    <DeleteDayBtn onClick={() => removeDay(dIdx)}>Delete Day</DeleteDayBtn>
                    <CitySelect value={day.city} onChange={e => updateCity(dIdx, e.target.value)}>
                      {CITIES.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </CitySelect>
                    <TemplateSelect defaultValue="" onChange={e => applyTemplate(dIdx, e.target.value)}>
                      <option value="" disabled>Templates</option>
                      {(dayTemplatesByCity[day.city] || []).slice(0, 5).map((tmpl, idx) => (
                        <option key={idx} value={idx}>{tmpl.label}</option>
                      ))}
                    </TemplateSelect>
                  </DayHeader>
                  {(() => {
                    const itemsArray = Object.values(day.slots)
                      .flat()
                      .filter(item => {
                        // Check if coordinates are valid numbers
                        const lat = Number(item.lat);
                        const lng = Number(item.lng);
                        return !isNaN(lat) && !isNaN(lng) && 
                               lat !== 0 && lng !== 0 && 
                               Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
                      })
                      .map(item => {
                        // Prepare photos array with proper structure
                        const photos = [];
                        
                        // Handle different photo formats
                        if (item.photos) {
                          if (Array.isArray(item.photos)) {
                            // If photos is an array, map to our format
                            photos.push(...item.photos.map(photo => ({
                              url: photo.getUrl ? photo.getUrl() : (photo.url || photo),
                              source: 'google_places',
                              attribution: 'Google Places'
                            })));
                          } else if (typeof item.photos === 'string') {
                            // If it's a single URL string
                            photos.push({
                              url: item.photos,
                              source: 'custom',
                              attribution: ''
                            });
                          }
                        }
                        
                        // Prepare types array
                        const types = [];
                        if (item.types && Array.isArray(item.types)) {
                          types.push(...item.types);
                        } else if (item.type) {
                          types.push(item.type);
                        }
                        if (types.length === 0) types.push('point_of_interest');
                        
                        // Prepare opening hours
                        let opening_hours = {
                          open_now: false,
                          weekday_text: []
                        };
                        
                        if (item.opening_hours) {
                          if (item.opening_hours.weekday_text) {
                            opening_hours.weekday_text = item.opening_hours.weekday_text;
                          }
                          if (item.opening_hours.open_now !== undefined) {
                            opening_hours.open_now = item.opening_hours.open_now;
                          }
                        }
                        
                        // Return the formatted item
                        return {
                          ...item,
                          id: item.id || `poi-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                          name: item.title || item.name || 'Unnamed Location',
                          description: item.desc || item.description || '',
                          types: [...new Set(types)],
                          photos: photos.length > 0 ? photos : null,
                          rating: typeof item.rating === 'number' ? item.rating : 0,
                          user_ratings_total: typeof item.user_ratings_total === 'number' ? item.user_ratings_total : 0,
                          price_level: typeof item.price_level === 'number' ? item.price_level : 0,
                          opening_hours,
                          vicinity: item.vicinity || item.address || item.desc || 'Location',
                          website: item.website || '',
                          phone: item.phone || '',
                          // Ensure lat/lng are numbers
                          lat: Number(item.lat),
                          lng: Number(item.lng),
                          // Preserve original data
                          originalData: item
                        };
                      });
                    
                    return (
                      <div key={`map-container-${dIdx}`} style={{
                        margin: '1rem 0',
                        height: '400px',
                        position: 'relative',
                        width: '100%',
                        minHeight: '400px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <MapComponent 
                          key={`map-${dIdx}-${mapKey}`}
                          items={itemsArray}
                          center={itemsArray[0] ? [itemsArray[0].lng, itemsArray[0].lat] : undefined}
                          zoom={itemsArray.length > 1 ? 12 : 14}
                          onMarkerClick={(item) => setSelectedPoi(item)}
                          showDetailedInfo={true}
                        />
                      </div>
                    );
                  })()}
                {expandedDays[dIdx] && TIME_SLOTS.map(slot => (
                  <Slot key={slot.key}>
                    <SlotLabel>{slot.label}</SlotLabel>
                    <Droppable droppableId={`${dIdx}-${slot.key}`}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {(day.slots[slot.key] || []).map((item, iIdx) => (
                            <Draggable
                              key={`${dIdx}-${slot.key}-${iIdx}`}
                              draggableId={`${dIdx}-${slot.key}-${iIdx}`}
                              index={iIdx}
                            >
                              {(provided) => (
                                <ActivityCard
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  $hasReservation={!!item.reservation}
                                  $reservationStatus={item.reservation?.status}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div>
                                    <ActivitySelect
                                      value={item.title || ''}
                                      onChange={e => {
                                        const selected = (activitiesByCity[day.city] || []).find(a => a.title === e.target.value);
                                        if (selected) {
                                          const slots = { ...timelineDays[dIdx].slots };
                                          const items = [...slots[slot.key]];
                                          items[iIdx] = {
                                            ...items[iIdx],
                                            ...selected,
                                            title: selected.title,
                                            desc: selected.desc || '',
                                            lat: selected.lat,
                                            lng: selected.lng,
                                            rating: selected.rating || 0,
                                            place_id: selected.place_id || ''
                                          };
                                          slots[slot.key] = items;
                                          updateDay(dIdx, slots);
                                        }
                                      }}
                                    >
                                      <option value="">Select an activity...</option>
                                      {(activitiesByCity[day.city] || []).map(opt => (
                                        <option key={opt.title} value={opt.title}>
                                          {opt.title}
                                        </option>
                                      ))}
                                    </ActivitySelect>
                                    <InfoBox style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'flex-start',
                                      gap: '0.75rem',
                                      padding: '1rem',
                                      backgroundColor: item.isPrivateTour ? '#f8f9ff' : colors.background,
                                      borderRadius: '0.5rem',
                                      border: item.isPrivateTour ? '1px solid #e0e7ff' : 'none',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    }}>
                                      <Title>
                                        {item.title}
                                        {item.isPrivateTour && (
                                          <PrivateTourBadge>
                                            <FiMapPin size={10} />
                                            Private Tour
                                          </PrivateTourBadge>
                                        )}
                                        {item.rating > 0 && (
                                          <span className="rating">
                                            <FiStar size={12} />
                                            {item.rating.toFixed(1)}
                                          </span>
                                        )}
                                      </Title>
                                      
                                      {/* Show Find Table button only for restaurants */}
                                      {isRestaurant(item) && (
                                        <ReserveButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRestaurantReservation(item, dIdx, slot.key);
                                          }}
                                        >
                                          <FiCalendar size={12} /> Find Table
                                        </ReserveButton>
                                      )}
                                      
                                      {item.display?.highlights && (
                                        <div style={{ marginTop: '0.5rem' }}>
                                          <div style={{ 
                                            fontWeight: '600', 
                                            color: '#4f46e5',
                                            marginBottom: '0.3rem',
                                            fontSize: '0.9rem'
                                          }}>
                                            Tour Highlights:
                                          </div>
                                          <ul style={{ 
                                            margin: '0.3rem 0 0 1.2rem',
                                            padding: 0,
                                            listStyleType: 'disc',
                                            color: '#4b5563',
                                            fontSize: '0.9rem',
                                            lineHeight: '1.5'
                                          }}>
                                            {item.display.highlights.map((highlight, idx) => (
                                              <li key={idx} style={{ marginBottom: '0.3rem' }}>
                                                {highlight}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      
                                      {item.desc && (
                                        <div className="activity-desc" style={{
                                          color: item.isPrivateTour ? '#4b5563' : '#6c757d',
                                          fontSize: '0.9rem',
                                          lineHeight: '1.5'
                                        }}>
                                          {item.desc}
                                        </div>
                                      )}
                                      
                                      {(item.display?.duration || item.display?.price) && (
                                        <div style={{
                                          display: 'flex',
                                          gap: '1.5rem',
                                          marginTop: '0.5rem',
                                          fontSize: '0.9rem',
                                          color: '#4b5563'
                                        }}>
                                          {item.display.duration && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                              <FiClock size={14} />
                                              <span>{item.display.duration}</span>
                                            </div>
                                          )}
                                          {item.display.price && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                              <FiDollarSign size={14} />
                                              <span>{item.display.price}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </InfoBox>
                                    <div className="activity-actions">
                                      <a 
                                        href={(() => {
                                          const name = item.name || item.title || '';
                                          const city = day.city || '';
                                          
                                          if (item.place_id) {
                                            return `https://www.google.com/maps/place/?q=place_id:${item.place_id}`;
                                          }
                                          
                                          if (item.lat && item.lng) {
                                            let query = name;
                                            if (item.address) query += ` ${item.address}`;
                                            if (city) query += ` ${city} Portugal`;
                                            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
                                          }
                                          
                                          return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name} ${city} Portugal`)}`;
                                        })()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="details-btn"
                                        onClick={(e) => {
                                          // Handle click for mouse users
                                          e.preventDefault();
                                          e.stopPropagation();
                                          window.open(e.currentTarget.href, '_blank', 'noopener,noreferrer');
                                        }}
                                        onMouseDown={(e) => {
                                          // Prevent drag start when clicking the button
                                          e.stopPropagation();
                                        }}
                                        onTouchStart={(e) => {
                                          // Prevent drag start and add active state
                                          e.stopPropagation();
                                          e.currentTarget.classList.add('active');
                                        }}
                                        onTouchEnd={(e) => {
                                          e.stopPropagation();
                                          e.currentTarget.classList.remove('active');
                                          // Manually trigger click on touch end
                                          if (!e.defaultPrevented) {
                                            window.open(e.currentTarget.href, '_blank', 'noopener,noreferrer');
                                          }
                                        }}
                                        onTouchCancel={(e) => {
                                          e.currentTarget.classList.remove('active');
                                        }}
                                        title="View on Google Maps"
                                      >
                                        <FiInfo size={14} />
                                        <span>Details</span>
                                      </a>
                                      <RemoveBtn onClick={() => removeActivity(dIdx, slot.key, iIdx)}>Remove</RemoveBtn>
                                    </div>
                                  </div>
                                </ActivityCard>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                    <AddBtn onClick={() => addActivity(dIdx, slot.key)}>+ Add Activity</AddBtn>
                  </Slot>
                ))}
              </TimelineWrapper>
              <RightColumn>
                <SectionTitle>Contextual Tips & Data</SectionTitle>

                <SubTitle>Opening Hours</SubTitle>
                <ul style={{ paddingLeft: 16, margin: 0 }}>
                  {Object.values(day.slots).flat().map((item, idx) => (
                    <li key={idx}>{item.title}: Open 9am–5pm</li>
                  ))}
                </ul>
                <SubTitle>Estimated Costs</SubTitle>
                <ul style={{ paddingLeft: 16, margin: 0 }}>
                  {Object.values(day.slots).flat().map((item, idx) => (
                    <li key={idx}>{item.title}: €15</li>
                  ))}
                </ul>
                <SectionTitle>Routing & Timing</SectionTitle>
                <SubTitle>Optimal Sequence</SubTitle>
                <div style={{ fontSize: '0.9rem', marginBottom: 8 }}>Auto‑arranged to minimize back‑tracking</div>
                <SubTitle>Live Transit</SubTitle>
                <div style={{ fontSize: '0.9rem', marginBottom: 8 }}>Bus ~7 min | Train ~15 min</div>
                <MapBtn onClick={() => openMaps(dIdx)} style={{ marginTop: '0.8rem' }}>
                  See Directions
                </MapBtn>
              </RightColumn>
            </DayRow>
            </React.Fragment>
            );
          })}
            <AddBtn onClick={() => addDay()}>+ Add Day</AddBtn>
          </LeftColumn>
        </Main>
      </DragDropContext>
    </React.Fragment>
  );
}
